import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthStateService } from '../services/auth-state.service';
import { AuthApiService } from '../services/auth-api.service';
import { PermissionsService } from '../services/permissions.service';
import { LoggingService } from '../services/logging.service';

/**
 * Authentication guard
 * Protects routes that require authentication
 * Redirects to login if user is not authenticated
 * 
 * If token exists but user data is not loaded, attempts to load user data
 * 
 * Usage in routes:
 * ```
 * {
 *   path: 'dashboard',
 *   component: DashboardComponent,
 *   canActivate: [authGuard]
 * }
 * ```
 */
export const authGuard: CanActivateFn = async (route, state) => {
  const authState = inject(AuthStateService);
  const authApi = inject(AuthApiService);
  const permissionsService = inject(PermissionsService);
  const router = inject(Router);
  const logger = inject(LoggingService);

  logger.info('Auth guard checking access', { 
    url: state.url, 
    isAuthenticated: authState.isAuthenticated() 
  });

  // Check if user is already authenticated
  if (authState.isAuthenticated()) {
    logger.info('User is authenticated, checking permissions');
    // Load permissions if not already loaded
    const user = authState.currentUser();
    if (user && permissionsService.permissions().length === 0) {
      logger.info('Loading permissions for user', { userId: user._id });
      await permissionsService.loadPermissions(user._id!);
    }
    logger.info('Access granted');
    return true;
  }

  // Check if we have a token but no user data (e.g., page refresh)
  const token = authState.getAccessToken();
  logger.info('Checking for stored token', { hasToken: !!token });
  
  if (token) {
    try {
      logger.info('Token found, attempting to load user data');
      
      // Try to load current user with token
      const user = await new Promise((resolve, reject) => {
        authApi.loadCurrentUser(token).subscribe({
          next: (userData) => resolve(userData),
          error: (error) => reject(error)
        });
      });

      // If successful, update auth state
      if (user) {
        authState.signIn(user as any, {
          accessToken: token,
          tokenType: 'Bearer',
          expiresIn: 3600
        });
        
        // Load permissions for the user - use _id for consistency
        const userId = (user as any)._id || (user as any).id;
        if (userId) {
          await permissionsService.loadPermissions(userId);
        } else {
          logger.error('User ID not found, cannot load permissions', user);
        }
        
        logger.info('User data and permissions loaded successfully from token');
        return true;
      }
    } catch (error) {
      logger.error('Failed to load user with token', error);
      logger.info('Clearing invalid token and auth state');
      // Clear invalid token
      authState.signOut(false);
    }
  }

  logger.warn('Authentication failed - redirecting to login', { 
    url: state.url,
    hasToken: !!token,
    reason: token ? 'Token validation failed' : 'No token found'
  });

  // Redirect to login with return URL
  return router.createUrlTree(['/auth/login'], {
    queryParams: { returnUrl: state.url },
  });
};